import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    Form,
    Input,
    InputNumber,
    Layout,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Typography,
    Avatar,
    Divider,
    Tooltip,
    Badge,
    Progress,
    message
} from "antd";

import {
    DeleteOutlined,
    EditOutlined,
    FilePdfOutlined,
    PlusOutlined,
    PrinterOutlined,
    ReloadOutlined,
    SearchOutlined,
    WalletOutlined,
    DollarOutlined,
    TeamOutlined,
    RiseOutlined,
    UserOutlined,
    CalendarOutlined,
    BankOutlined,
    SafetyCertificateOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    FileTextOutlined,
    SettingOutlined,
    CloseCircleOutlined
} from "@ant-design/icons";

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    CartesianGrid
} from "recharts";

import Swal from "sweetalert2";

import {
    PDFDownloadLink
} from "@react-pdf/renderer";

import PayrollSlipGenerationPDF from "./PayrollSlipGenerationPDF";
import SalaryAdvance from "./SalaryAdvance";

import {
    useAcademicYear
} from "../../AcademicYearContext";

import api from "../../api/api";

const {
    Content
} = Layout;

const {
    Title,
    Text
} = Typography;

const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];

const COLORS = {
    black: "#09090b",
    deepBlack: "#111113",
    charcoal: "#27272a",
    orange: "#f97316",
    deepOrange: "#ea580c",
    green: "#16a34a",
    lime: "#84cc16",
    blue: "#2563eb",
    red: "#dc2626",
    gold: "#eab308",
    white: "#ffffff",
    background: "#f5f7fa",
    soft: "#f8fafc",
    border: "#e5e7eb",
    muted: "#71717a"
};

const initialFormData = {
    teacher_id: null,
    support_staff_id: null,
    month: "",
    basic_salary: 0,
    night_school_commission: 0,
    salary_advance: 0,
    salary_advance_record: null,
    salary_advance_balance: 0,
    income_tax: 0,
    social_security_tax: 0,
    total_deductions: 0
};

const formatMoney = (value) => {
    const number = parseFloat(value || 0);

    return `$${number.toLocaleString(
        "en-US",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }
    )}`;
};

function Payroll() {
    const {
        selectedYear
    } = useAcademicYear();

    const [form] = Form.useForm();

    const [salaryAdvanceBalance, setSalaryAdvanceBalance] =
        useState(0);

    const [salaryAdvanceRecord, setSalaryAdvanceRecord] =
        useState(null);

    const [salaryAdvanceLoading, setSalaryAdvanceLoading] =
        useState(false);

    const [payrolls, setPayrolls] =
        useState([]);

    const [teachers, setTeachers] =
        useState([]);

    const [supportStaff, setSupportStaff] =
        useState([]);

    const [loading, setLoading] =
        useState(false);

    const [tableLoading, setTableLoading] =
        useState(false);

    const [openModal, setOpenModal] =
        useState(false);

    const [editingPayroll, setEditingPayroll] =
        useState(null);

    const [showSalaryAdvance, setShowSalaryAdvance] =
        useState(false);

    const [filters, setFilters] =
        useState({
            search: "",
            month: ""
        });

    const [selectedStaffType, setSelectedStaffType] =
        useState(null);

    const fetchSalaryAdvanceBalance = async ({
        teacherId = null,
        supportStaffId = null,
        month = null
    } = {}) => {
        if (
            !selectedYear?.id ||
            !month ||
            (!teacherId && !supportStaffId)
        ) {
            return;
        }

        setSalaryAdvanceLoading(true);

        try {
            const response = await api.get(
                "/salary-advance-balance/",
                {
                    params: {
                        teacher_id:
                            teacherId || undefined,

                        support_staff_id:
                            supportStaffId || undefined,

                        month,

                        academic_year:
                            selectedYear.id
                    }
                }
            );

            const balance = Number(
                response.data?.remaining_balance || 0
            );

            const recordId =
                response.data?.salary_advance_id ||
                null;

            setSalaryAdvanceBalance(
                balance
            );

            setSalaryAdvanceRecord(
                recordId
            );

            form.setFieldsValue({
                salary_advance:
                    balance,

                salary_advance_record:
                    recordId
            });
        } catch (error) {
            console.error(
                "SALARY ADVANCE ERROR:",
                error.response?.data || error
            );

            setSalaryAdvanceBalance(0);
            setSalaryAdvanceRecord(null);

            form.setFieldsValue({
                salary_advance: 0,
                salary_advance_record: null
            });
        } finally {
            setSalaryAdvanceLoading(false);
        }
    };

    const fetchPayrolls = async () => {
        if (!selectedYear?.id) {
            return;
        }

        setTableLoading(true);

        try {
            const response = await api.get(
                "/payrolls/",
                {
                    params: {
                        academic_year:
                            selectedYear.id
                    }
                }
            );

            const data =
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || [];

            setPayrolls(data);
        } catch (error) {
            console.error(
                "PAYROLL FETCH ERROR:",
                error.response?.data || error
            );

            message.error(
                "Failed to load payroll records."
            );
        } finally {
            setTableLoading(false);
        }
    };

    const fetchTeachers = async () => {
        try {
            const response = await api.get(
                "/api/users/teacher-data/"
            );

            const data =
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || [];

            setTeachers(data);
        } catch (error) {
            console.error(
                "TEACHERS FETCH ERROR:",
                error
            );

            message.error(
                "Failed to load teachers."
            );
        }
    };

    const fetchSupportStaff = async () => {
        try {
            const response = await api.get(
                "/supportstaff/"
            );

            const data =
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || [];

            setSupportStaff(data);
        } catch (error) {
            console.error(
                "SUPPORT STAFF FETCH ERROR:",
                error
            );

            message.error(
                "Failed to load support staff."
            );
        }
    };

    useEffect(() => {
        if (selectedYear?.id) {
            fetchPayrolls();
        }
    }, [
        selectedYear?.id
    ]);

    useEffect(() => {
        fetchTeachers();
        fetchSupportStaff();
    }, []);

    const getStaffName = (
        payroll
    ) => {
        return (
            payroll.teacher?.full_name ||
            payroll.support_staff?.full_name ||
            "Unknown Staff"
        );
    };

    const getStaffType = (
        payroll
    ) => {
        return payroll.teacher
            ? "Teacher"
            : payroll.support_staff
                ? "Support Staff"
                : "Staff";
    };

    const getInitial = (
        name
    ) => {
        return (
            name ||
            "?"
        )
            .charAt(0)
            .toUpperCase();
    };

    const openCreateModal = () => {
        setEditingPayroll(null);
        setSelectedStaffType(null);

        setSalaryAdvanceBalance(0);
        setSalaryAdvanceRecord(null);

        form.resetFields();

        form.setFieldsValue({
            ...initialFormData
        });

        setOpenModal(true);
    };

    const openEditModal = (
        payroll
    ) => {
        setEditingPayroll(
            payroll
        );

        const teacherId =
            payroll.teacher?.id ||
            null;

        const supportStaffId =
            payroll.support_staff?.id ||
            null;

        setSelectedStaffType(
            teacherId
                ? "teacher"
                : supportStaffId
                    ? "support_staff"
                    : null
        );

        const advance =
            Number(
                payroll.salary_advance || 0
            );

        setSalaryAdvanceBalance(
            advance
        );

        setSalaryAdvanceRecord(
            payroll.salary_advance_record ||
            null
        );

        form.setFieldsValue({
            teacher_id:
                teacherId,

            support_staff_id:
                supportStaffId,

            month:
                payroll.month || "",

            basic_salary:
                Number(
                    payroll.basic_salary || 0
                ),

            night_school_commission:
                Number(
                    payroll.night_school_commission ||
                    0
                ),

            salary_advance:
                advance,

            salary_advance_record:
                payroll.salary_advance_record ||
                null,

            income_tax:
                Number(
                    payroll.income_tax || 0
                ),

            social_security_tax:
                Number(
                    payroll.social_security_tax ||
                    0
                ),

            total_deductions:
                Number(
                    payroll.total_deductions || 0
                )
        });

        setOpenModal(true);
    };

    const closeModal = () => {
        setOpenModal(false);
        setEditingPayroll(null);
        setSelectedStaffType(null);

        setSalaryAdvanceBalance(0);
        setSalaryAdvanceRecord(null);

        form.resetFields();
    };

    const handleTeacherChange = async (
        teacherId
    ) => {
        setSelectedStaffType(
            teacherId
                ? "teacher"
                : null
        );

        const currentMonth =
            form.getFieldValue(
                "month"
            );

        form.setFieldsValue({
            teacher_id:
                teacherId,

            support_staff_id:
                null,

            salary_advance:
                0,

            salary_advance_record:
                null
        });

        setSalaryAdvanceBalance(0);
        setSalaryAdvanceRecord(null);

        const teacher =
            teachers.find(
                (item) =>
                    item.id ===
                    teacherId
            );

        if (teacher) {
            form.setFieldsValue({
                basic_salary:
                    Number(
                        teacher.salary ||
                        0
                    )
            });
        }

        if (
            teacherId &&
            currentMonth
        ) {
            await fetchSalaryAdvanceBalance({
                teacherId,
                month:
                    currentMonth
            });
        }
    };

    const handleSupportStaffChange =
        async (
            staffId
        ) => {
            setSelectedStaffType(
                staffId
                    ? "support_staff"
                    : null
            );

            const currentMonth =
                form.getFieldValue(
                    "month"
                );

            const staff =
                supportStaff.find(
                    (item) =>
                        item.id ===
                        staffId
                );

            form.setFieldsValue({
                support_staff_id:
                    staffId ||
                    null,

                teacher_id:
                    null,

                basic_salary:
                    Number(
                        staff?.salary ||
                        0
                    ),

                salary_advance:
                    0,

                salary_advance_record:
                    null
            });

            setSalaryAdvanceBalance(0);
            setSalaryAdvanceRecord(null);

            if (
                staffId &&
                currentMonth
            ) {
                await fetchSalaryAdvanceBalance({
                    supportStaffId:
                        staffId,

                    month:
                        currentMonth
                });
            }
        };

    const handleMonthChange =
        async (
            month
        ) => {
            const teacherId =
                form.getFieldValue(
                    "teacher_id"
                );

            const supportStaffId =
                form.getFieldValue(
                    "support_staff_id"
                );

            if (teacherId) {
                await fetchSalaryAdvanceBalance({
                    teacherId,
                    month
                });
            }

            if (supportStaffId) {
                await fetchSalaryAdvanceBalance({
                    supportStaffId,
                    month
                });
            }
        };

    const handleSubmit =
        async (
            values
        ) => {
            if (
                !values.teacher_id &&
                !values.support_staff_id
            ) {
                message.error(
                    "Please select a teacher or support staff."
                );

                return;
            }

            if (!selectedYear?.id) {
                message.error(
                    "Please select an academic year."
                );

                return;
            }

            const deduction =
                Number(
                    values.salary_advance ||
                    0
                );

            const outstandingBalance =
                Number(
                    salaryAdvanceBalance ||
                    0
                );

            if (
                deduction >
                outstandingBalance
            ) {
                message.error(
                    "Salary advance deduction cannot be greater than the outstanding balance."
                );

                return;
            }

            setLoading(true);

            try {
                const payload = {
                    teacher_id:
                        values.teacher_id ||
                        null,

                    support_staff_id:
                        values.support_staff_id ||
                        null,

                    month:
                        values.month,

                    basic_salary:
                        Number(
                            values.basic_salary ||
                            0
                        ),

                    night_school_commission:
                        Number(
                            values.night_school_commission ||
                            0
                        ),

                    salary_advance:
                        deduction,

                    salary_advance_record:
                        values.salary_advance_record ||
                        salaryAdvanceRecord ||
                        null,

                    income_tax:
                        Number(
                            values.income_tax ||
                            0
                        ),

                    social_security_tax:
                        Number(
                            values.social_security_tax ||
                            0
                        )
                };

                const response =
                    editingPayroll
                        ? await api.put(
                            `/payrolls/${editingPayroll.id}/`,
                            payload
                        )
                        : await api.post(
                            "/payrolls/",
                            payload
                        );

                if (
                    editingPayroll
                ) {
                    setPayrolls(
                        (
                            previous
                        ) =>
                            previous.map(
                                (
                                    item
                                ) =>
                                    item.id ===
                                    editingPayroll.id
                                        ? response.data
                                        : item
                            )
                    );

                    message.success(
                        "Payroll updated successfully."
                    );
                } else {
                    setPayrolls(
                        (
                            previous
                        ) => [
                            response.data,
                            ...previous
                        ]
                    );

                    message.success(
                        "Payroll created successfully."
                    );
                }

                closeModal();
            } catch (error) {
                console.error(
                    "PAYROLL SUBMIT ERROR:",
                    error.response?.data ||
                    error
                );

                Swal.fire({
                    icon:
                        "error",

                    title:
                        "Payroll Error",

                    text:
                        typeof error.response?.data ===
                        "object"
                            ? JSON.stringify(
                                error.response.data
                            )
                            : (
                                error.response?.data ||
                                "Unable to save payroll."
                            )
                });
            } finally {
                setLoading(false);
            }
        };

    const handleDelete =
        async (
            id
        ) => {
            const confirmation =
                await Swal.fire({
                    title:
                        "Delete Payroll?",

                    text:
                        "This payroll record will be permanently deleted.",

                    icon:
                        "warning",

                    showCancelButton:
                        true,

                    confirmButtonText:
                        "Delete",

                    cancelButtonText:
                        "Cancel",

                    confirmButtonColor:
                        COLORS.orange
                });

            if (
                !confirmation.isConfirmed
            ) {
                return;
            }

            try {
                await api.delete(
                    `/payrolls/${id}/`
                );

                setPayrolls(
                    (
                        previous
                    ) =>
                        previous.filter(
                            (
                                item
                            ) =>
                                item.id !==
                                id
                        )
                );

                message.success(
                    "Payroll deleted successfully."
                );
            } catch (error) {
                console.error(
                    error
                );

                message.error(
                    "Failed to delete payroll."
                );
            }
        };

    const handlePrint =
        (
            payroll
        ) => {
            const newWindow =
                window.open(
                    "",
                    "_blank"
                );

            if (!newWindow) {
                message.warning(
                    "Please allow pop-ups to print payroll."
                );

                return;
            }

            const staffName =
                getStaffName(
                    payroll
                );

            newWindow.document.write(`
                <html>
                    <head>
                        <title>
                            Payroll - ${staffName}
                        </title>

                        <style>
                            body {
                                font-family:
                                    Arial,
                                    sans-serif;
                                padding: 40px;
                                color: #111;
                                background: #fff;
                            }

                            .header {
                                border-bottom:
                                    4px solid #f97316;
                                padding-bottom:
                                    20px;
                                margin-bottom:
                                    25px;
                            }

                            h1 {
                                margin: 0 0 8px;
                                font-size: 28px;
                            }

                            .subtitle {
                                color: #666;
                            }

                            .staff {
                                margin:
                                    25px 0;
                                padding:
                                    18px;
                                background:
                                    #f8fafc;
                                border:
                                    1px solid #ddd;
                                border-radius:
                                    10px;
                            }

                            table {
                                width: 100%;
                                border-collapse:
                                    collapse;
                                margin-top:
                                    25px;
                            }

                            th,
                            td {
                                border:
                                    1px solid #ddd;
                                padding:
                                    13px;
                                text-align:
                                    left;
                            }

                            th {
                                background:
                                    #18181b;
                                color:
                                    white;
                            }

                            .total {
                                font-weight:
                                    bold;
                                font-size:
                                    18px;
                                background:
                                    #fff7ed;
                            }

                            .footer {
                                margin-top:
                                    35px;
                                text-align:
                                    center;
                                color:
                                    #777;
                                font-size:
                                    12px;
                            }
                        </style>
                    </head>

                    <body>

                        <div class="header">
                            <h1>
                                Staff Payroll Statement
                            </h1>

                            <div class="subtitle">
                                Academic Year:
                                ${
                                    payroll.academic_year?.name ||
                                    selectedYear?.name ||
                                    ""
                                }
                            </div>
                        </div>

                        <div class="staff">
                            <strong>
                                Staff:
                            </strong>
                            ${staffName}

                            <br />

                            <strong>
                                Staff Type:
                            </strong>
                            ${getStaffType(
                                payroll
                            )}

                            <br />

                            <strong>
                                Payroll Month:
                            </strong>
                            ${payroll.month || ""}
                        </div>

                        <table>

                            <tr>
                                <th>
                                    Basic Salary
                                </th>

                                <td>
                                    ${formatMoney(
                                        payroll.basic_salary
                                    )}
                                </td>
                            </tr>

                            <tr>
                                <th>
                                    Bonus / Compensation
                                </th>

                                <td>
                                    ${formatMoney(
                                        payroll.night_school_commission
                                    )}
                                </td>
                            </tr>

                            <tr>
                                <th>
                                    Salary Advance
                                </th>

                                <td>
                                    ${formatMoney(
                                        payroll.salary_advance
                                    )}
                                </td>
                            </tr>

                            <tr>
                                <th>
                                    Income Tax
                                </th>

                                <td>
                                    ${formatMoney(
                                        payroll.income_tax
                                    )}
                                </td>
                            </tr>

                            <tr>
                                <th>
                                    Social Security
                                </th>

                                <td>
                                    ${formatMoney(
                                        payroll.social_security_tax
                                    )}
                                </td>
                            </tr>

                            <tr>
                                <th>
                                    Total Deductions
                                </th>

                                <td>
                                    ${formatMoney(
                                        payroll.total_deductions
                                    )}
                                </td>
                            </tr>

                            <tr class="total">
                                <th>
                                    Total Paid
                                </th>

                                <td>
                                    ${formatMoney(
                                        payroll.total_paid
                                    )}
                                </td>
                            </tr>

                        </table>

                        <div class="footer">
                            Payroll generated by
                            Polu Integrated School
                            Management System
                        </div>

                    </body>
                </html>
            `);

            newWindow.document.close();
            newWindow.focus();
            newWindow.print();
        };

    const filteredPayrolls =
        useMemo(() => {
            return payrolls.filter(
                (
                    payroll
                ) => {
                    const staffName =
                        getStaffName(
                            payroll
                        );

                    const matchesSearch =
                        !filters.search ||
                        staffName
                            .toLowerCase()
                            .includes(
                                filters.search
                                    .toLowerCase()
                            );

                    const matchesMonth =
                        !filters.month ||
                        payroll.month
                            ?.toLowerCase() ===
                        filters.month
                            .toLowerCase();

                    return (
                        matchesSearch &&
                        matchesMonth
                    );
                }
            );
        }, [
            payrolls,
            filters
        ]);

    const statistics =
        useMemo(() => {
            const totalPayroll =
                filteredPayrolls.reduce(
                    (
                        total,
                        item
                    ) =>
                        total +
                        Number(
                            item.total_paid ||
                            0
                        ),
                    0
                );

            const totalDeductions =
                filteredPayrolls.reduce(
                    (
                        total,
                        item
                    ) =>
                        total +
                        Number(
                            item.total_deductions ||
                            0
                        ),
                    0
                );

            const totalAdvances =
                filteredPayrolls.reduce(
                    (
                        total,
                        item
                    ) =>
                        total +
                        Number(
                            item.salary_advance ||
                            0
                        ),
                    0
                );

            const totalBasic =
                filteredPayrolls.reduce(
                    (
                        total,
                        item
                    ) =>
                        total +
                        Number(
                            item.basic_salary ||
                            0
                        ),
                    0
                );

            return {
                totalPayroll,
                totalDeductions,
                totalAdvances,
                totalBasic,
                staffCount:
                    filteredPayrolls.length
            };
        }, [
            filteredPayrolls
        ]);

    const deductionRate =
        statistics.totalPayroll > 0
            ? Math.min(
                100,
                Math.round(
                    (
                        statistics.totalDeductions /
                        (
                            statistics.totalBasic ||
                            1
                        )
                    ) *
                    100
                )
            )
            : 0;

    const chartData =
        useMemo(() => {
            const grouped =
                filteredPayrolls.reduce(
                    (
                        accumulator,
                        item
                    ) => {
                        const key =
                            item.month ||
                            "Unknown";

                        if (
                            !accumulator[key]
                        ) {
                            accumulator[key] = {
                                month:
                                    key,
                                total:
                                    0
                            };
                        }

                        accumulator[key].total +=
                            Number(
                                item.total_paid ||
                                0
                            );

                        return accumulator;
                    },
                    {}
                );

            return MONTHS
                .filter(
                    (
                        month
                    ) =>
                        grouped[
                            month
                        ]
                )
                .map(
                    (
                        month
                    ) => ({
                        month,
                        total:
                            grouped[
                                month
                            ].total
                    })
                );
        }, [
            filteredPayrolls
        ]);

    const columns = [
        {
            title:
                "Staff Member",

            key:
                "staff",

            fixed:
                "left",

            width:
                260,

            render:
                (
                    _,
                    record
                ) => {
                    const name =
                        getStaffName(
                            record
                        );

                    const type =
                        getStaffType(
                            record
                        );

                    return (
                        <Space
                            size={12}
                        >
                            <Avatar
                                size={44}
                                style={{
                                    background:
                                        type ===
                                        "Teacher"
                                            ? "linear-gradient(135deg,#f97316,#ea580c)"
                                            : "linear-gradient(135deg,#2563eb,#1d4ed8)",
                                    fontWeight:
                                        800
                                }}
                            >
                                {getInitial(
                                    name
                                )}
                            </Avatar>

                            <div>
                                <Text
                                    strong
                                    style={{
                                        display:
                                            "block"
                                    }}
                                >
                                    {name}
                                </Text>

                                <Tag
                                    bordered={false}
                                    color={
                                        type ===
                                        "Teacher"
                                            ? "orange"
                                            : "blue"
                                    }
                                    style={{
                                        marginTop:
                                            4,
                                        borderRadius:
                                            20,
                                        fontSize:
                                            11
                                    }}
                                >
                                    {type}
                                </Tag>
                            </div>
                        </Space>
                    );
                }
        },

        {
            title:
                "Month",

            dataIndex:
                "month",

            key:
                "month",

            width:
                130,

            render:
                (
                    month
                ) => (
                    <Tag
                        color="orange"
                        style={{
                            borderRadius:
                                20,
                            padding:
                                "4px 11px",
                            fontWeight:
                                600
                        }}
                    >
                        <CalendarOutlined
                            style={{
                                marginRight:
                                    5
                            }}
                        />

                        {month}
                    </Tag>
                )
        },

        {
            title:
                "Basic Salary",

            dataIndex:
                "basic_salary",

            key:
                "basic_salary",

            width:
                145,

            align:
                "right",

            render:
                (
                    value
                ) => (
                    <Text
                        strong
                    >
                        {formatMoney(
                            value
                        )}
                    </Text>
                )
        },

        {
            title:
                "Bonus",

            dataIndex:
                "night_school_commission",

            key:
                "bonus",

            width:
                135,

            align:
                "right",

            render:
                (
                    value
                ) => (
                    <Text
                        style={{
                            color:
                                COLORS.green,
                            fontWeight:
                                600
                        }}
                    >
                        +{" "}
                        {formatMoney(
                            value
                        )}
                    </Text>
                )
        },

        {
            title:
                "Deductions",

            dataIndex:
                "total_deductions",

            key:
                "deductions",

            width:
                145,

            align:
                "right",

            render:
                (
                    value
                ) => (
                    <Text
                        style={{
                            color:
                                COLORS.red,
                            fontWeight:
                                700
                        }}
                    >
                        -
                        {" "}
                        {formatMoney(
                            value
                        )}
                    </Text>
                )
        },

        {
            title:
                "Advance",

            dataIndex:
                "salary_advance",

            key:
                "advance",

            width:
                135,

            align:
                "right",

            render:
                (
                    value
                ) => (
                    <Text
                        style={{
                            color:
                                COLORS.red,
                            fontWeight:
                                600
                        }}
                    >
                        {formatMoney(
                            value
                        )}
                    </Text>
                )
        },

        {
            title:
                "Income Tax",

            dataIndex:
                "income_tax",

            key:
                "income_tax",

            width:
                135,

            align:
                "right",

            render:
                (
                    value
                ) =>
                    formatMoney(
                        value
                    )
        },

        {
            title:
                "Social Security",

            dataIndex:
                "social_security_tax",

            key:
                "social_security",

            width:
                155,

            align:
                "right",

            render:
                (
                    value
                ) =>
                    formatMoney(
                        value
                    )
        },

        {
            title:
                "Net Paid",

            dataIndex:
                "total_paid",

            key:
                "total_paid",

            width:
                160,

            align:
                "right",

            render:
                (
                    value
                ) => (
                    <Text
                        strong
                        style={{
                            color:
                                COLORS.green,
                            fontSize:
                                15
                        }}
                    >
                        {formatMoney(
                            value
                        )}
                    </Text>
                )
        },

        {
            title:
                "Actions",

            key:
                "actions",

            fixed:
                "right",

            width:
                155,

            render:
                (
                    _,
                    record
                ) => (
                    <Space
                        size={2}
                    >
                        <Tooltip
                            title="Edit payroll"
                        >
                            <Button
                                type="text"
                                icon={
                                    <EditOutlined />
                                }
                                onClick={() =>
                                    openEditModal(
                                        record
                                    )
                                }
                            />
                        </Tooltip>

                        <Tooltip
                            title="Print payroll"
                        >
                            <Button
                                type="text"
                                icon={
                                    <PrinterOutlined />
                                }
                                onClick={() =>
                                    handlePrint(
                                        record
                                    )
                                }
                            />
                        </Tooltip>

                        <Tooltip
                            title="Delete payroll"
                        >
                            <Button
                                type="text"
                                danger
                                icon={
                                    <DeleteOutlined />
                                }
                                onClick={() =>
                                    handleDelete(
                                        record.id
                                    )
                                }
                            />
                        </Tooltip>
                    </Space>
                )
        }
    ];

    return (
        <Layout
            style={{
                minHeight:
                    "100vh",
                background:
                    "linear-gradient(180deg,#f8fafc 0%,#eef2f6 100%)"
            }}
        >
            <Content
                style={{
                    padding:
                        "24px",
                    maxWidth:
                        1700,
                    width:
                        "100%",
                    margin:
                        "0 auto"
                }}
            >
                <div
                    style={{
                        display:
                            "flex",
                        justifyContent:
                            "space-between",
                        alignItems:
                            "center",
                        gap:
                            20,
                        flexWrap:
                            "wrap",
                        padding:
                            "28px 30px",
                        borderRadius:
                            24,
                        background:
                            "linear-gradient(135deg,#09090b 0%,#18181b 55%,#ea580c 150%)",
                        boxShadow:
                            "0 18px 45px rgba(15,23,42,0.15)",
                        position:
                            "relative",
                        overflow:
                            "hidden"
                    }}
                >
                    <div
                        style={{
                            position:
                                "absolute",
                            width:
                                280,
                            height:
                                280,
                            borderRadius:
                                "50%",
                            background:
                                "rgba(249,115,22,0.12)",
                            right:
                                -100,
                            top:
                                -150
                        }}
                    />

                    <div
                        style={{
                            position:
                                "relative",
                            zIndex:
                                1
                        }}
                    >
                        <Space
                            direction="vertical"
                            size={5}
                        >
                            <Space
                                size={10}
                            >
                                <div
                                    style={{
                                        width:
                                            46,
                                        height:
                                            46,
                                        borderRadius:
                                            14,
                                        display:
                                            "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",
                                        background:
                                            "linear-gradient(135deg,#f97316,#84cc16)",
                                        color:
                                            "#fff",
                                        fontSize:
                                            21
                                    }}
                                >
                                    <BankOutlined />
                                </div>

                                <Tag
                                    color="green"
                                    bordered={false}
                                    style={{
                                        borderRadius:
                                            20,
                                        fontWeight:
                                            700
                                    }}
                                >
                                    FINANCE
                                </Tag>
                            </Space>

                            <Title
                                level={2}
                                style={{
                                    margin:
                                        "10px 0 0",
                                    color:
                                        "#fff",
                                    fontWeight:
                                        800
                                }}
                            >
                                Staff Payroll Management
                            </Title>

                            <Text
                                style={{
                                    color:
                                        "rgba(255,255,255,0.65)"
                                }}
                            >
                                Manage compensation, deductions,
                                salary advances and payroll
                                reporting from one workspace.
                            </Text>
                        </Space>
                    </div>

                    <div
                        style={{
                            position:
                                "relative",
                            zIndex:
                                1,
                            minWidth:
                                210,
                            padding:
                                "15px 18px",
                            borderRadius:
                                15,
                            background:
                                "rgba(255,255,255,0.08)",
                            border:
                                "1px solid rgba(255,255,255,0.12)"
                        }}
                    >
                        <Text
                            style={{
                                display:
                                    "block",
                                color:
                                    "rgba(255,255,255,0.55)",
                                fontSize:
                                    11,
                                fontWeight:
                                    700,
                                letterSpacing:
                                    "0.8px"
                            }}
                        >
                            ACTIVE ACADEMIC YEAR
                        </Text>

                        <Text
                            style={{
                                color:
                                    "#fff",
                                fontSize:
                                    20,
                                fontWeight:
                                    800
                            }}
                        >
                            {selectedYear?.name ||
                                "Not Selected"}
                        </Text>
                    </div>
                </div>

                <Row
                    gutter={[
                        16,
                        16
                    ]}
                    style={{
                        marginTop:
                            18
                    }}
                >
                    <Col
                        xs={24}
                        sm={12}
                        xl={6}
                    >
                        <Card
                            bordered={false}
                            style={{
                                borderRadius:
                                    18,
                                height:
                                    "100%",
                                background:
                                    "linear-gradient(135deg,#ea580c,#f97316)",
                                boxShadow:
                                    "0 10px 25px rgba(249,115,22,0.20)"
                            }}
                        >
                            <Statistic
                                title={
                                    <Text
                                        style={{
                                            color:
                                                "rgba(255,255,255,0.72)"
                                        }}
                                    >
                                        Total Net Payroll
                                    </Text>
                                }
                                value={
                                    statistics.totalPayroll
                                }
                                precision={
                                    2
                                }
                                prefix="$"
                                valueStyle={{
                                    color:
                                        "#fff",
                                    fontWeight:
                                        800,
                                    fontSize:
                                        27
                                }}
                            />

                            <Text
                                style={{
                                    color:
                                        "rgba(255,255,255,0.62)",
                                    fontSize:
                                        12
                                }}
                            >
                                Total amount paid
                            </Text>
                        </Card>
                    </Col>

                    <Col
                        xs={24}
                        sm={12}
                        xl={6}
                    >
                        <Card
                            bordered={false}
                            style={{
                                borderRadius:
                                    18,
                                height:
                                    "100%",
                                background:
                                    "#fff",
                                border:
                                    "1px solid #e5e7eb",
                                boxShadow:
                                    "0 8px 25px rgba(15,23,42,0.06)"
                            }}
                        >
                            <Statistic
                                title={
                                    <Text type="secondary">
                                        Staff Payroll Records
                                    </Text>
                                }
                                value={
                                    statistics.staffCount
                                }
                                prefix={
                                    <TeamOutlined
                                        style={{
                                            color:
                                                COLORS.blue
                                        }}
                                    />
                                }
                                valueStyle={{
                                    color:
                                        COLORS.black,
                                    fontWeight:
                                        800,
                                    fontSize:
                                        27
                                }}
                            />

                            <Text
                                type="secondary"
                                style={{
                                    fontSize:
                                        12
                                }}
                            >
                                Current filtered records
                            </Text>
                        </Card>
                    </Col>

                    <Col
                        xs={24}
                        sm={12}
                        xl={6}
                    >
                        <Card
                            bordered={false}
                            style={{
                                borderRadius:
                                    18,
                                height:
                                    "100%",
                                background:
                                    "linear-gradient(135deg,#65a30d,#22c55e)",
                                boxShadow:
                                    "0 10px 25px rgba(34,197,94,0.16)"
                            }}
                        >
                            <Statistic
                                title={
                                    <Text
                                        style={{
                                            color:
                                                "rgba(255,255,255,0.72)"
                                        }}
                                    >
                                        Total Deductions
                                    </Text>
                                }
                                value={
                                    statistics.totalDeductions
                                }
                                precision={
                                    2
                                }
                                prefix="$"
                                valueStyle={{
                                    color:
                                        "#fff",
                                    fontWeight:
                                        800,
                                    fontSize:
                                        27
                                }}
                            />

                            <Text
                                style={{
                                    color:
                                        "rgba(255,255,255,0.65)",
                                    fontSize:
                                        12
                                }}
                            >
                                Taxes and deductions
                            </Text>
                        </Card>
                    </Col>

                    <Col
                        xs={24}
                        sm={12}
                        xl={6}
                    >
                        <Card
                            bordered={false}
                            style={{
                                borderRadius:
                                    18,
                                height:
                                    "100%",
                                background:
                                    "linear-gradient(135deg,#18181b,#27272a)",
                                boxShadow:
                                    "0 10px 25px rgba(0,0,0,0.12)"
                            }}
                        >
                            <Statistic
                                title={
                                    <Text
                                        style={{
                                            color:
                                                "rgba(255,255,255,0.60)"
                                        }}
                                    >
                                        Salary Advances
                                    </Text>
                                }
                                value={
                                    statistics.totalAdvances
                                }
                                precision={
                                    2
                                }
                                prefix="$"
                                valueStyle={{
                                    color:
                                        "#fff",
                                    fontWeight:
                                        800,
                                    fontSize:
                                        27
                                }}
                            />

                            <Text
                                style={{
                                    color:
                                        "rgba(255,255,255,0.48)",
                                    fontSize:
                                        12
                                }}
                            >
                                Advance deductions
                            </Text>
                        </Card>
                    </Col>
                </Row>

                {showSalaryAdvance ? (
                    <Card
                        bordered={false}
                        style={{
                            marginTop:
                                20,
                            borderRadius:
                                20,
                            boxShadow:
                                "0 8px 30px rgba(15,23,42,0.06)"
                        }}
                    >
                        <Button
                            icon={
                                <ReloadOutlined />
                            }
                            onClick={() =>
                                setShowSalaryAdvance(
                                    false
                                )
                            }
                            style={{
                                marginBottom:
                                    20
                            }}
                        >
                            Back To Payroll
                        </Button>

                        <SalaryAdvance />
                    </Card>
                ) : (
                    <>
                        <Card
                            bordered={false}
                            style={{
                                marginTop:
                                    20,
                                borderRadius:
                                    20,
                                boxShadow:
                                    "0 8px 30px rgba(15,23,42,0.06)"
                            }}
                        >
                            <Row
                                gutter={[
                                    12,
                                    12
                                ]}
                                align="middle"
                            >
                                <Col
                                    xs={24}
                                    lg={8}
                                >
                                    <Input
                                        size="large"
                                        prefix={
                                            <SearchOutlined
                                                style={{
                                                    color:
                                                        COLORS.orange
                                                }}
                                            />
                                        }
                                        placeholder="Search staff member..."
                                        value={
                                            filters.search
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setFilters(
                                                (
                                                    previous
                                                ) => ({
                                                    ...previous,
                                                    search:
                                                        event
                                                            .target
                                                            .value
                                                })
                                            )
                                        }
                                        allowClear
                                        style={{
                                            borderRadius:
                                                12
                                        }}
                                    />
                                </Col>

                                <Col
                                    xs={24}
                                    sm={12}
                                    lg={4}
                                >
                                    <Select
                                        size="large"
                                        placeholder="All months"
                                        value={
                                            filters.month ||
                                            undefined
                                        }
                                        onChange={(
                                            value
                                        ) =>
                                            setFilters(
                                                (
                                                    previous
                                                ) => ({
                                                    ...previous,
                                                    month:
                                                        value ||
                                                        ""
                                                })
                                            )
                                        }
                                        allowClear
                                        options={
                                            MONTHS.map(
                                                (
                                                    month
                                                ) => ({
                                                    label:
                                                        month,
                                                    value:
                                                        month
                                                })
                                            )
                                        }
                                        style={{
                                            width:
                                                "100%"
                                        }}
                                    />
                                </Col>

                                <Col
                                    xs={24}
                                    sm={12}
                                    lg={12}
                                >
                                    <div
                                        style={{
                                            display:
                                                "flex",
                                            justifyContent:
                                                "flex-end",
                                            gap:
                                                9,
                                            flexWrap:
                                                "wrap"
                                        }}
                                    >
                                        <Button
                                            size="large"
                                            icon={
                                                <ReloadOutlined />
                                            }
                                            onClick={
                                                fetchPayrolls
                                            }
                                            loading={
                                                tableLoading
                                            }
                                        >
                                            Refresh
                                        </Button>

                                        <Button
                                            size="large"
                                            icon={
                                                <WalletOutlined />
                                            }
                                            onClick={() =>
                                                setShowSalaryAdvance(
                                                    true
                                                )
                                            }
                                        >
                                            Salary Advances
                                        </Button>

                                        <Button
                                            size="large"
                                            type="primary"
                                            icon={
                                                <PlusOutlined />
                                            }
                                            onClick={
                                                openCreateModal
                                            }
                                            style={{
                                                background:
                                                    COLORS.orange,
                                                borderColor:
                                                    COLORS.orange,
                                                borderRadius:
                                                    10,
                                                fontWeight:
                                                    700
                                            }}
                                        >
                                            Create Payroll
                                        </Button>

                                        <PDFDownloadLink
                                            document={
                                                <PayrollSlipGenerationPDF
                                                    payrolls={
                                                        filteredPayrolls
                                                    }
                                                    month={
                                                        filters.month
                                                    }
                                                    academicYear={
                                                        selectedYear?.name
                                                    }
                                                />
                                            }
                                            fileName={`Payslips_${filters.month || "All_Months"}_${selectedYear?.name || "Academic_Year"}.pdf`}
                                        >
                                            {({
                                                loading:
                                                    pdfLoading
                                            }) => (
                                                <Button
                                                    size="large"
                                                    danger
                                                    icon={
                                                        <FilePdfOutlined />
                                                    }
                                                >
                                                    {pdfLoading
                                                        ? "Generating..."
                                                        : "PDF Report"}
                                                </Button>
                                            )}
                                        </PDFDownloadLink>
                                    </div>
                                </Col>
                            </Row>
                        </Card>

                        <Row
                            gutter={[
                                16,
                                16
                            ]}
                            style={{
                                marginTop:
                                    18
                            }}
                        >
                            <Col
                                xs={24}
                                lg={16}
                            >
                                <Card
                                    bordered={false}
                                    style={{
                                        height:
                                            "100%",
                                        borderRadius:
                                            20,
                                        boxShadow:
                                            "0 8px 30px rgba(15,23,42,0.05)"
                                    }}
                                >
                                    <Space
                                        direction="vertical"
                                        size={3}
                                    >
                                        <Text
                                            type="secondary"
                                            style={{
                                                fontSize:
                                                    12,
                                                fontWeight:
                                                    700,
                                                letterSpacing:
                                                    "0.7px"
                                            }}
                                        >
                                            PAYROLL OVERVIEW
                                        </Text>

                                        <Title
                                            level={4}
                                            style={{
                                                margin:
                                                    0
                                            }}
                                        >
                                            Current Payroll Position
                                        </Title>
                                    </Space>

                                    <Row
                                        gutter={[
                                            20,
                                            20
                                        ]}
                                        style={{
                                            marginTop:
                                                20
                                        }}
                                    >
                                        <Col
                                            xs={24}
                                            sm={8}
                                        >
                                            <div
                                                style={{
                                                    padding:
                                                        15,
                                                    borderRadius:
                                                        14,
                                                    background:
                                                        "#f8fafc"
                                                }}
                                            >
                                                <Text
                                                    type="secondary"
                                                >
                                                    Gross Salary
                                                </Text>

                                                <Title
                                                    level={4}
                                                    style={{
                                                        margin:
                                                            "5px 0",
                                                        color:
                                                            COLORS.black
                                                    }}
                                                >
                                                    {formatMoney(
                                                        statistics.totalBasic
                                                    )}
                                                </Title>

                                                <Text
                                                    type="secondary"
                                                    style={{
                                                        fontSize:
                                                            11
                                                    }}
                                                >
                                                    Before deductions
                                                </Text>
                                            </div>
                                        </Col>

                                        <Col
                                            xs={24}
                                            sm={8}
                                        >
                                            <div
                                                style={{
                                                    padding:
                                                        15,
                                                    borderRadius:
                                                        14,
                                                    background:
                                                        "#fff7ed"
                                                }}
                                            >
                                                <Text
                                                    type="secondary"
                                                >
                                                    Deductions
                                                </Text>

                                                <Title
                                                    level={4}
                                                    style={{
                                                        margin:
                                                            "5px 0",
                                                        color:
                                                            COLORS.orange
                                                    }}
                                                >
                                                    {formatMoney(
                                                        statistics.totalDeductions
                                                    )}
                                                </Title>

                                                <Progress
                                                    percent={
                                                        deductionRate
                                                    }
                                                    size="small"
                                                    showInfo={
                                                        false
                                                    }
                                                    strokeColor={
                                                        COLORS.orange
                                                    }
                                                />
                                            </div>
                                        </Col>

                                        <Col
                                            xs={24}
                                            sm={8}
                                        >
                                            <div
                                                style={{
                                                    padding:
                                                        15,
                                                    borderRadius:
                                                        14,
                                                    background:
                                                        "#f0fdf4"
                                                }}
                                            >
                                                <Text
                                                    type="secondary"
                                                >
                                                    Net Payroll
                                                </Text>

                                                <Title
                                                    level={4}
                                                    style={{
                                                        margin:
                                                            "5px 0",
                                                        color:
                                                            COLORS.green
                                                    }}
                                                >
                                                    {formatMoney(
                                                        statistics.totalPayroll
                                                    )}
                                                </Title>

                                                <Text
                                                    type="secondary"
                                                    style={{
                                                        fontSize:
                                                            11
                                                    }}
                                                >
                                                    After deductions
                                                </Text>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>

                            <Col
                                xs={24}
                                lg={8}
                            >
                                <Card
                                    bordered={false}
                                    style={{
                                        height:
                                            "100%",
                                        borderRadius:
                                            20,
                                        background:
                                            "linear-gradient(135deg,#18181b,#27272a)",
                                        boxShadow:
                                            "0 8px 30px rgba(0,0,0,0.12)"
                                    }}
                                >
                                    <Space
                                        direction="vertical"
                                        size={4}
                                    >
                                        <Text
                                            style={{
                                                color:
                                                    "rgba(255,255,255,0.50)",
                                                fontSize:
                                                    12,
                                                fontWeight:
                                                    700
                                            }}
                                        >
                                            PAYROLL STATUS
                                        </Text>

                                        <Title
                                            level={4}
                                            style={{
                                                color:
                                                    "#fff",
                                                margin:
                                                    0
                                            }}
                                        >
                                            {statistics.staffCount > 0
                                                ? "Payroll Records Available"
                                                : "No Payroll Records"}
                                        </Title>
                                    </Space>

                                    <Divider
                                        style={{
                                            borderColor:
                                                "rgba(255,255,255,0.10)"
                                        }}
                                    />

                                    <Space
                                        direction="vertical"
                                        size={13}
                                    >
                                        <Space>
                                            <Badge
                                                status={
                                                    statistics.staffCount >
                                                    0
                                                        ? "success"
                                                        : "default"
                                                }
                                            />

                                            <Text
                                                style={{
                                                    color:
                                                        "rgba(255,255,255,0.70)"
                                                }}
                                            >
                                                {statistics.staffCount}{" "}
                                                payroll records
                                            </Text>
                                        </Space>

                                        <Space>
                                            <CalendarOutlined
                                                style={{
                                                    color:
                                                        COLORS.orange
                                                }}
                                            />

                                            <Text
                                                style={{
                                                    color:
                                                        "rgba(255,255,255,0.70)"
                                                }}
                                            >
                                                {filters.month ||
                                                    "All months"}
                                            </Text>
                                        </Space>

                                        <Space>
                                            <SafetyCertificateOutlined
                                                style={{
                                                    color:
                                                        COLORS.lime
                                                }}
                                            />

                                            <Text
                                                style={{
                                                    color:
                                                        "rgba(255,255,255,0.70)"
                                                }}
                                            >
                                                Academic year:
                                                {" "}
                                                {selectedYear?.name ||
                                                    "N/A"}
                                            </Text>
                                        </Space>
                                    </Space>
                                </Card>
                            </Col>
                        </Row>

                        <Card
                            bordered={false}
                            style={{
                                marginTop:
                                    18,
                                borderRadius:
                                    20,
                                overflow:
                                    "hidden",
                                boxShadow:
                                    "0 8px 30px rgba(15,23,42,0.05)"
                            }}
                            bodyStyle={{
                                padding:
                                    0
                            }}
                        >
                            <div
                                style={{
                                    padding:
                                        "20px 22px",
                                    borderBottom:
                                        "1px solid #edf0f3"
                                }}
                            >
                                <Row
                                    justify="space-between"
                                    align="middle"
                                    gutter={[
                                        10,
                                        10
                                    ]}
                                >
                                    <Col>
                                        <Space
                                            size={12}
                                        >
                                            <div
                                                style={{
                                                    width:
                                                        42,
                                                    height:
                                                        42,
                                                    borderRadius:
                                                        12,
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    justifyContent:
                                                        "center",
                                                    background:
                                                        "rgba(249,115,22,0.10)",
                                                    color:
                                                        COLORS.orange
                                                }}
                                            >
                                                <FileTextOutlined />
                                            </div>

                                            <div>
                                                <Title
                                                    level={4}
                                                    style={{
                                                        margin:
                                                            0
                                                    }}
                                                >
                                                    Payroll Records
                                                </Title>

                                                <Text
                                                    type="secondary"
                                                >
                                                    Staff compensation
                                                    records for the selected
                                                    academic year.
                                                </Text>
                                            </div>
                                        </Space>
                                    </Col>

                                    <Col>
                                        <Tag
                                            color="blue"
                                            style={{
                                                borderRadius:
                                                    20,
                                                padding:
                                                    "4px 12px",
                                                fontWeight:
                                                    600
                                            }}
                                        >
                                            {filteredPayrolls.length}{" "}
                                            Records
                                        </Tag>
                                    </Col>
                                </Row>
                            </div>

                            <Table
                                rowKey="id"
                                loading={
                                    tableLoading
                                }
                                columns={
                                    columns
                                }
                                dataSource={
                                    filteredPayrolls
                                }
                                scroll={{
                                    x:
                                        1750
                                }}
                                pagination={{
                                    pageSize:
                                        10,
                                    showSizeChanger:
                                        true,
                                    pageSizeOptions:
                                        [
                                            "10",
                                            "20",
                                            "50",
                                            "100"
                                        ],
                                    showQuickJumper:
                                        true,
                                    showTotal:
                                        (
                                            total
                                        ) =>
                                            `${total} payroll records`
                                }}
                                locale={{
                                    emptyText:
                                        (
                                            <div
                                                style={{
                                                    padding:
                                                        "55px 20px"
                                                }}
                                            >
                                                <Empty
                                                    description={
                                                        selectedYear?.id
                                                            ? "No payroll records found"
                                                            : "Please select an academic year"
                                                    }
                                                />
                                            </div>
                                        )
                                }}
                            />
                        </Card>

                        <Card
                            bordered={false}
                            style={{
                                marginTop:
                                    18,
                                borderRadius:
                                    20,
                                boxShadow:
                                    "0 8px 30px rgba(15,23,42,0.05)"
                            }}
                        >
                            <Row
                                justify="space-between"
                                align="middle"
                            >
                                <Col>
                                    <Space
                                        direction="vertical"
                                        size={2}
                                    >
                                        <Title
                                            level={4}
                                            style={{
                                                margin:
                                                    0
                                            }}
                                        >
                                            Monthly Payroll Summary
                                        </Title>

                                        <Text
                                            type="secondary"
                                        >
                                            Net payroll paid across
                                            recorded months.
                                        </Text>
                                    </Space>
                                </Col>

                                <Col>
                                    <Tag
                                        color="orange"
                                        style={{
                                            borderRadius:
                                                20
                                        }}
                                    >
                                        {selectedYear?.name ||
                                            "Academic Year"}
                                    </Tag>
                                </Col>
                            </Row>

                            <div
                                style={{
                                    width:
                                        "100%",
                                    height:
                                        340,
                                    marginTop:
                                        20
                                }}
                            >
                                {chartData.length >
                                0 ? (
                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                    >
                                        <BarChart
                                            data={
                                                chartData
                                            }
                                            margin={{
                                                top:
                                                    10,
                                                right:
                                                    20,
                                                left:
                                                    0,
                                                bottom:
                                                    10
                                            }}
                                        >
                                            <CartesianGrid
                                                strokeDasharray="3 3"
                                                vertical={
                                                    false
                                                }
                                            />

                                            <XAxis
                                                dataKey="month"
                                                tick={{
                                                    fontSize:
                                                        12
                                                }}
                                            />

                                            <YAxis
                                                tick={{
                                                    fontSize:
                                                        12
                                                }}
                                            />

                                            <RechartsTooltip
                                                formatter={(
                                                    value
                                                ) =>
                                                    formatMoney(
                                                        value
                                                    )
                                                }
                                            />

                                            <Bar
                                                dataKey="total"
                                                fill={
                                                    COLORS.orange
                                                }
                                                radius={[
                                                    8,
                                                    8,
                                                    0,
                                                    0
                                                ]}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Empty
                                        image={
                                            Empty.PRESENTED_IMAGE_SIMPLE
                                        }
                                        description="No monthly payroll data available."
                                        style={{
                                            paddingTop:
                                                80
                                        }}
                                    />
                                )}
                            </div>
                        </Card>
                    </>
                )}

                <div
                    style={{
                        textAlign:
                            "center",
                        padding:
                            "22px 5px 5px"
                    }}
                >
                    <Space
                        size={8}
                        wrap
                        style={{
                            justifyContent:
                                "center"
                        }}
                    >
                        <SafetyCertificateOutlined
                            style={{
                                color:
                                    COLORS.green
                            }}
                        />

                        <Text
                            type="secondary"
                            style={{
                                fontSize:
                                    12
                            }}
                        >
                            Secure Payroll Management
                        </Text>

                        <Text
                            type="secondary"
                        >
                            •
                        </Text>

                        <Text
                            type="secondary"
                            style={{
                                fontSize:
                                    12
                            }}
                        >
                            Academic Year:{" "}
                            {selectedYear?.name ||
                                "N/A"}
                        </Text>
                    </Space>

                    <Text
                        type="secondary"
                        style={{
                            display:
                                "block",
                            marginTop:
                                5,
                            fontSize:
                                11
                        }}
                    >
                        Design by Polu Integrated
                        School Management System
                    </Text>
                </div>

                <Modal
                    open={
                        openModal
                    }
                    title={
                        <Space
                            size={12}
                        >
                            <div
                                style={{
                                    width:
                                        44,
                                    height:
                                        44,
                                    borderRadius:
                                        12,
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    background:
                                        "linear-gradient(135deg,#f97316,#ea580c)",
                                    color:
                                        "#fff",
                                    fontSize:
                                        20
                                }}
                            >
                                <DollarOutlined />
                            </div>

                            <div>
                                <Title
                                    level={4}
                                    style={{
                                        margin:
                                            0
                                    }}
                                >
                                    {editingPayroll
                                        ? "Edit Payroll"
                                        : "Create Payroll"}
                                </Title>

                                <Text
                                    type="secondary"
                                >
                                    {selectedYear?.name ||
                                        "Academic Year"}
                                </Text>
                            </div>
                        </Space>
                    }
                    onCancel={
                        closeModal
                    }
                    footer={
                        null
                    }
                    width={
                        900
                    }
                    centered
                    destroyOnClose
                >
                    <Alert
                        message="Payroll staff assignment"
                        description="A payroll record must belong to either a Teacher or Support Staff member. Select only one."
                        type="info"
                        showIcon
                        style={{
                            marginBottom:
                                24,
                            borderRadius:
                                12
                        }}
                    />

                    <Form
                        form={
                            form
                        }
                        layout="vertical"
                        onFinish={
                            handleSubmit
                        }
                    >
                        <Row
                            gutter={[
                                16,
                                0
                            ]}
                        >
                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Teacher"
                                    name="teacher_id"
                                >
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select teacher"
                                        disabled={
                                            selectedStaffType ===
                                            "support_staff"
                                        }
                                        optionFilterProp="label"
                                        onChange={
                                            handleTeacherChange
                                        }
                                        options={
                                            teachers.map(
                                                (
                                                    teacher
                                                ) => ({
                                                    label:
                                                        teacher.full_name,
                                                    value:
                                                        teacher.id
                                                })
                                            )
                                        }
                                        style={{
                                            borderRadius:
                                                10
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Support Staff"
                                    name="support_staff_id"
                                >
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select support staff"
                                        disabled={
                                            selectedStaffType ===
                                            "teacher"
                                        }
                                        optionFilterProp="label"
                                        onChange={
                                            handleSupportStaffChange
                                        }
                                        options={
                                            supportStaff.map(
                                                (
                                                    staff
                                                ) => ({
                                                    label:
                                                        staff.full_name,
                                                    value:
                                                        staff.id
                                                })
                                            )
                                        }
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Payroll Month"
                                    name="month"
                                    rules={[
                                        {
                                            required:
                                                true,
                                            message:
                                                "Please select a month."
                                        }
                                    ]}
                                >
                                    <Select
                                        placeholder="Select payroll month"
                                        onChange={
                                            handleMonthChange
                                        }
                                        options={
                                            MONTHS.map(
                                                (
                                                    month
                                                ) => ({
                                                    label:
                                                        month,
                                                    value:
                                                        month
                                                })
                                            )
                                        }
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Basic Salary"
                                    name="basic_salary"
                                >
                                    <InputNumber
                                        style={{
                                            width:
                                                "100%"
                                        }}
                                        prefix="$"
                                        readOnly
                                        min={
                                            0
                                        }
                                        precision={
                                            2
                                        }
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Bonus / Compensation"
                                    name="night_school_commission"
                                >
                                    <InputNumber
                                        style={{
                                            width:
                                                "100%"
                                        }}
                                        prefix="$"
                                        min={
                                            0
                                        }
                                        precision={
                                            2
                                        }
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Salary Advance Deduction"
                                    name="salary_advance"
                                    extra={
                                        salaryAdvanceLoading
                                            ? "Checking outstanding advance..."
                                            : salaryAdvanceBalance >
                                                0
                                                ? `Outstanding balance: ${formatMoney(
                                                    salaryAdvanceBalance
                                                )}`
                                                : "No outstanding salary advance"
                                    }
                                >
                                    <InputNumber
                                        style={{
                                            width:
                                                "100%"
                                        }}
                                        min={
                                            0
                                        }
                                        precision={
                                            2
                                        }
                                        prefix="$"
                                        disabled={
                                            salaryAdvanceLoading
                                        }
                                        max={
                                            salaryAdvanceBalance ||
                                            undefined
                                        }
                                        onChange={(
                                            value
                                        ) => {
                                            const deduction =
                                                Number(
                                                    value ||
                                                    0
                                                );

                                            if (
                                                deduction >
                                                salaryAdvanceBalance
                                            ) {
                                                message.warning(
                                                    "The deduction cannot be greater than the outstanding salary advance."
                                                );
                                            }
                                        }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="salary_advance_record"
                                    hidden
                                >
                                    <Input />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Income Tax"
                                    name="income_tax"
                                >
                                    <InputNumber
                                        style={{
                                            width:
                                                "100%"
                                        }}
                                        prefix="$"
                                        min={
                                            0
                                        }
                                        precision={
                                            2
                                        }
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Social Security Tax"
                                    name="social_security_tax"
                                >
                                    <InputNumber
                                        style={{
                                            width:
                                                "100%"
                                        }}
                                        prefix="$"
                                        min={
                                            0
                                        }
                                        precision={
                                            2
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider />

                        <div
                            style={{
                                display:
                                    "flex",
                                justifyContent:
                                    "flex-end",
                                gap:
                                    10,
                                flexWrap:
                                    "wrap"
                            }}
                        >
                            <Button
                                onClick={
                                    closeModal
                                }
                                icon={
                                    <CloseCircleOutlined />
                                }
                            >
                                Cancel
                            </Button>

                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={
                                    loading
                                }
                                icon={
                                    editingPayroll
                                        ? <EditOutlined />
                                        : <PlusOutlined />
                                }
                                style={{
                                    background:
                                        COLORS.orange,
                                    borderColor:
                                        COLORS.orange,
                                    fontWeight:
                                        700
                                }}
                            >
                                {editingPayroll
                                    ? "Update Payroll"
                                    : "Create Payroll"}
                            </Button>
                        </div>
                    </Form>
                </Modal>
            </Content>
        </Layout>
    );
}

export default Payroll;






// import React, { useEffect, useMemo, useState } from "react";

// import {
//     Alert,
//     AutoComplete,
//     Button,
//     Card,
//     Col,
//     Empty,
//     Form,
//     Input,
//     InputNumber,
//     Layout,
//     Modal,
//     Row,
//     Select,
//     Space,
//     Statistic,
//     Table,
//     Tag,
//     Typography,
//     message,
// } from "antd";

// import {
//     DeleteOutlined,
//     EditOutlined,
//     FilePdfOutlined,
//     PlusOutlined,
//     PrinterOutlined,
//     ReloadOutlined,
//     SearchOutlined,
//     WalletOutlined,
//     DollarOutlined,
//     TeamOutlined,
//     RiseOutlined,
// } from "@ant-design/icons";

// import {
//     BarChart,
//     Bar,
//     XAxis,
//     YAxis,
//     Tooltip,
//     ResponsiveContainer,
// } from "recharts";
// import Swal from "sweetalert2";
// import { PDFDownloadLink } from "@react-pdf/renderer";
// import PayrollSlipGenerationPDF from "./PayrollSlipGenerationPDF";
// import SalaryAdvance from "./SalaryAdvance";
// import { useAcademicYear } from "../../AcademicYearContext";
// import api from "../../api/api";
// import "./Payroll.css";


// const {
//     Content,
// } = Layout;


// const {
//     Title,
//     Text,
// } = Typography;


// const MONTHS = [
//     "January",
//     "February",
//     "March",
//     "April",
//     "May",
//     "June",
//     "July",
//     "August",
//     "September",
//     "October",
//     "November",
//     "December",
// ];


// const initialFormData = {
//     teacher_id: null,
//     support_staff_id: null,
//     month: "",
//     basic_salary: 0,
//     night_school_commission: 0,
//     salary_advance: 0,
//     salary_advance_record: null,
//     salary_advance_balance: 0,
//     income_tax: 0,
//     social_security_tax: 0,
//     total_deductions: 0,
// };


// function Payroll() {
//   const [salaryAdvanceBalance, setSalaryAdvanceBalance] = useState(0);
// const [salaryAdvanceRecord, setSalaryAdvanceRecord] = useState(null);
// const [salaryAdvanceLoading, setSalaryAdvanceLoading] = useState(false);

// const {selectedYear} = useAcademicYear();

// const [form] = Form.useForm();

// const [payrolls, setPayrolls] = useState([]);
// const [teachers, setTeachers] = useState([]);
// const [supportStaff, setSupportStaff] = useState([]);
// const [loading, setLoading] = useState(false);
// const [tableLoading, setTableLoading] = useState(false);
// const [openModal, setOpenModal] = useState(false);
// const [editingPayroll, setEditingPayroll] = useState(null);
// const [showSalaryAdvance, setShowSalaryAdvance] = useState(false);

// const [filters, setFilters] = useState({
//     search: "",
//     month: "",
// });

// const [selectedStaffType, setSelectedStaffType] = useState(null);
// const fetchSalaryAdvanceBalance = async ({
//     teacherId = null,
//     supportStaffId = null,
//     month = null,
// } = {}) => {
//     if (
//         !selectedYear?.id ||
//         !month ||
//         (!teacherId && !supportStaffId)
//     ) {
//         return;
//     }

//     setSalaryAdvanceLoading(true);

//     try {
//         const response = await api.get(
//             "/salary-advance-balance/",
//             {
//                 params: {
//                     teacher_id:
//                         teacherId || undefined,
//                     support_staff_id:
//                         supportStaffId || undefined,
//                     month,
//                     academic_year:
//                         selectedYear.id,
//                 },
//             }
//         );

//         console.log(
//             "SALARY ADVANCE RESPONSE:",
//             response.data
//         );

//         const balance = Number(
//             response.data?.remaining_balance || 0
//         );

//         const recordId =
//             response.data?.salary_advance_id || null;

//         setSalaryAdvanceBalance(balance);
//         setSalaryAdvanceRecord(recordId);

//         form.setFieldsValue({
//             salary_advance: balance,
//             salary_advance_record: recordId,
//         });
//     } catch (error) {
//         console.error(
//             "SALARY ADVANCE ERROR:",
//             error.response?.data || error
//         );

//         setSalaryAdvanceBalance(0);
//         setSalaryAdvanceRecord(null);

//         form.setFieldsValue({
//             salary_advance: 0,
//             salary_advance_record: null,
//         });
//     } finally {
//         setSalaryAdvanceLoading(false);
//     }
// };
//     /*
//     |--------------------------------------------------------------------------
//     | FETCH PAYROLLS
//     |--------------------------------------------------------------------------
//     */

//     const fetchPayrolls = async () => {

//         if (!selectedYear?.id) {

//             return;

//         }


//         setTableLoading(true);


//         try {

//             const response = await api.get(
//                 "/payrolls/",
//                 {
//                     params: {
//                         academic_year:
//                             selectedYear.id,
//                     },
//                 }
//             );


//             const data =
//                 Array.isArray(response.data)

//                     ? response.data

//                     : response.data?.results || [];


//             setPayrolls(data);

//         } catch (error) {

//             console.error(
//                 "PAYROLL FETCH ERROR:",
//                 error.response?.data || error
//             );


//             message.error(
//                 "Failed to load payroll records."
//             );

//         } finally {

//             setTableLoading(false);

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | FETCH TEACHERS
//     |--------------------------------------------------------------------------
//     */

//     const fetchTeachers = async () => {

//         try {

//             const response = await api.get(
//                 "/api/users/teacher-data/"
//             );


//             const data =
//                 Array.isArray(response.data)

//                     ? response.data

//                     : response.data?.results || [];


//             setTeachers(data);

//         } catch (error) {

//             console.error(
//                 "TEACHERS FETCH ERROR:",
//                 error
//             );


//             message.error(
//                 "Failed to load teachers."
//             );

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | FETCH SUPPORT STAFF
//     |--------------------------------------------------------------------------
//     */

//     const fetchSupportStaff = async () => {

//         try {

//             const response = await api.get(
//                 "/supportstaff/"
//             );


//             const data =
//                 Array.isArray(response.data)

//                     ? response.data

//                     : response.data?.results || [];


//             setSupportStaff(data);

//         } catch (error) {

//             console.error(
//                 "SUPPORT STAFF FETCH ERROR:",
//                 error
//             );


//             message.error(
//                 "Failed to load support staff."
//             );

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | EFFECTS
//     |--------------------------------------------------------------------------
//     */

//     useEffect(() => {

//         if (selectedYear?.id) {

//             fetchPayrolls();

//         }

//     }, [
//         selectedYear?.id,
//     ]);


//     useEffect(() => {

//         fetchTeachers();

//         fetchSupportStaff();

//     }, []);


//     /*
//     |--------------------------------------------------------------------------
//     | FORMAT MONEY
//     |--------------------------------------------------------------------------
//     */

//     const formatMoney = (
//         value
//     ) => {

//         const number =
//             parseFloat(value || 0);


//         return `$${number.toLocaleString(
//             "en-US",
//             {
//                 minimumFractionDigits: 2,
//                 maximumFractionDigits: 2,
//             }
//         )}`;

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | STAFF NAME
//     |--------------------------------------------------------------------------
//     */

//     const getStaffName = (
//         payroll
//     ) => {

//         return (

//             payroll.teacher?.full_name ||

//             payroll.support_staff?.full_name ||

//             "Unknown Staff"

//         );

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | OPEN CREATE MODAL
//     |--------------------------------------------------------------------------
//     */

//     const openCreateModal = () => {

//         setEditingPayroll(null);

//         setSelectedStaffType(null);

//         form.resetFields();

//         form.setFieldsValue({

//             ...initialFormData,

//         });

//         setOpenModal(true);

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | OPEN EDIT MODAL
//     |--------------------------------------------------------------------------
//     */

//     const openEditModal = (
//         payroll
//     ) => {

//         setEditingPayroll(payroll);


//         const teacherId =
//             payroll.teacher?.id || null;


//         const supportStaffId =
//             payroll.support_staff?.id || null;


//         setSelectedStaffType(

//             teacherId

//                 ? "teacher"

//                 : supportStaffId

//                     ? "support_staff"

//                     : null

//         );


//         form.setFieldsValue({

//             teacher_id:
//                 teacherId,

//             support_staff_id:
//                 supportStaffId,

//             month:
//                 payroll.month || "",

//             basic_salary:
//                 Number(
//                     payroll.basic_salary || 0
//                 ),

//             night_school_commission:
//                 Number(
//                     payroll.night_school_commission || 0
//                 ),

//             salary_advance:
//                 Number(
//                     payroll.salary_advance || 0
//                 ),

//             income_tax:
//                 Number(
//                     payroll.income_tax || 0
//                 ),

//             social_security_tax:
//                 Number(
//                     payroll.social_security_tax || 0
//                 ),

//             total_deductions:
//                 Number(
//                     payroll.total_deductions || 0
//                 ),

//         });


//         setOpenModal(true);

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | CLOSE MODAL
//     |--------------------------------------------------------------------------
//     */

//     const closeModal = () => {

//         setOpenModal(false);

//         setEditingPayroll(null);

//         setSelectedStaffType(null);

//         form.resetFields();

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | GET TEACHER SALARY
//     |--------------------------------------------------------------------------
//     */

//     const fetchTeacherSalary = async (
//         teacherId
//     ) => {

//         if (!teacherId) {

//             return;

//         }


//         try {

//             const response = await api.get(
//                 `/teacher-salary/${teacherId}/`
//             );


//             form.setFieldValue(
//                 "basic_salary",
//                 Number(
//                     response.data.salary || 0
//                 )
//             );

//         } catch (error) {

//             console.error(
//                 "SALARY FETCH ERROR:",
//                 error
//             );

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | TEACHER CHANGE
//     |--------------------------------------------------------------------------
//     */
// const handleTeacherChange = async (teacherId) => {
//     setSelectedStaffType(
//         teacherId ? "teacher" : null
//     );

//     const currentMonth =
//         form.getFieldValue("month");

//     form.setFieldsValue({
//         teacher_id: teacherId,
//         support_staff_id: null,
//         salary_advance: 0,
//         salary_advance_record: null,
//     });

//     setSalaryAdvanceBalance(0);
//     setSalaryAdvanceRecord(null);

//     const teacher = teachers.find(
//         (item) => item.id === teacherId
//     );

//     if (teacher) {
//         form.setFieldsValue({
//             basic_salary: Number(
//                 teacher.salary || 0
//             ),
//         });
//     }

//     if (
//         teacherId &&
//         currentMonth
//     ) {
//         await fetchSalaryAdvanceBalance({
//             teacherId,
//             month: currentMonth,
//         });
//     }
// };

//     /*
//     |--------------------------------------------------------------------------
//     | SUPPORT STAFF CHANGE
//     |--------------------------------------------------------------------------
//     */
// const handleSupportStaffChange = async (staffId) => {
//     setSelectedStaffType(staffId ? "support_staff" : null);

//     const currentMonth = form.getFieldValue("month");

//     const staff = supportStaff.find((item) => item.id === staffId);

//     form.setFieldsValue({
//         support_staff_id: staffId || null,
//         teacher_id: null,
//         basic_salary: Number(staff?.salary || 0),
//         salary_advance: 0,
//         salary_advance_record: null,
//     });

//     if (staffId && currentMonth) {
//         await fetchSalaryAdvanceBalance({
//             supportStaffId: staffId,
//             month: currentMonth,
//         });
//     }
// };

// // Handle Month Change:
// const handleMonthChange = async (month) => {
//     const teacherId = form.getFieldValue("teacher_id");
//     const supportStaffId = form.getFieldValue("support_staff_id");

//     if (teacherId) {
//         await fetchSalaryAdvanceBalance({
//             teacherId,
//             month,
//         });
//     }

//     if (supportStaffId) {
//         await fetchSalaryAdvanceBalance({
//             supportStaffId,
//             month,
//         });
//     }
// };

//     /*
//     |--------------------------------------------------------------------------
//     | SUBMIT PAYROLL
//     |--------------------------------------------------------------------------
//     */
// const handleSubmit = async (values) => {
//     if (!values.teacher_id && !values.support_staff_id) {
//         message.error("Please select a teacher or support staff.");
//         return;
//     }

//     if (!selectedYear?.id) {
//         message.error("Please select an academic year.");
//         return;
//     }
// const deduction = Number(
//     values.salary_advance || 0
// );

// const outstandingBalance = Number(
//     salaryAdvanceBalance || 0
// );

// if (deduction > outstandingBalance) {
//     message.error(
//         "Salary advance deduction cannot be greater than the outstanding balance."
//     );

//     return;
// }

//     setLoading(true);

//     try {
//   const payload = {
//     teacher_id: values.teacher_id || null,
//     support_staff_id: values.support_staff_id || null,
//     month: values.month,
//     basic_salary: Number(values.basic_salary || 0),
//     night_school_commission: Number(values.night_school_commission || 0),
//     salary_advance: deduction,
//     salary_advance_record: values.salary_advance_record || salaryAdvanceRecord || null,
//     income_tax: Number(values.income_tax || 0),
//     social_security_tax: Number(values.social_security_tax || 0),
// };

//         const response = editingPayroll
//             ? await api.put(
//                 `/payrolls/${editingPayroll.id}/`,
//                 payload
//             )
//             : await api.post(
//                 "/payrolls/",
//                 payload
//             );

//         if (editingPayroll) {
//             setPayrolls((previous) =>
//                 previous.map((item) =>
//                     item.id === editingPayroll.id
//                         ? response.data
//                         : item
//                 )
//             );

//             message.success(
//                 "Payroll updated successfully."
//             );
//         } else {
//             setPayrolls((previous) => [
//                 response.data,
//                 ...previous,
//             ]);

//             message.success(
//                 "Payroll created successfully."
//             );
//         }

//         closeModal();
//     } catch (error) {
//         console.error(
//             "PAYROLL SUBMIT ERROR:",
//             error.response?.data || error
//         );

//         Swal.fire({
//             icon: "error",
//             title: "Payroll Error",
//             text:
//                 typeof error.response?.data === "object"
//                     ? JSON.stringify(
//                         error.response.data
//                     )
//                     : (
//                         error.response?.data ||
//                         "Unable to save payroll."
//                     ),
//         });
//     } finally {
//         setLoading(false);
//     }
// };
//     /*
//     |--------------------------------------------------------------------------
//     | DELETE PAYROLL
//     |--------------------------------------------------------------------------
//     */

//     const handleDelete = async (
//         id
//     ) => {

//         const confirmation =
//             await Swal.fire({

//                 title:
//                     "Delete Payroll?",

//                 text:
//                     "This payroll record will be permanently deleted.",

//                 icon:
//                     "warning",

//                 showCancelButton:
//                     true,

//                 confirmButtonText:
//                     "Delete",

//                 confirmButtonColor:
//                     "#f97316",

//             });


//         if (
//             !confirmation.isConfirmed
//         ) {

//             return;

//         }


//         try {

//             await api.delete(
//                 `/payrolls/${id}/`
//             );


//             setPayrolls(
//                 (previous) =>

//                     previous.filter(
//                         (item) =>
//                             item.id !== id
//                     )

//             );


//             message.success(
//                 "Payroll deleted successfully."
//             );

//         } catch (error) {

//             console.error(
//                 error
//             );


//             message.error(
//                 "Failed to delete payroll."
//             );

//         }

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | PRINT PAYROLL
//     |--------------------------------------------------------------------------
//     */

//     const handlePrint = (
//         payroll
//     ) => {

//         const newWindow =
//             window.open(
//                 "",
//                 "_blank"
//             );


//         if (!newWindow) {

//             return;

//         }


//         const staffName =
//             getStaffName(payroll);


//         newWindow.document.write(`

//             <html>

//                 <head>

//                     <title>
//                         Payroll - ${staffName}
//                     </title>

//                     <style>

//                         body {
//                             font-family: Arial, sans-serif;
//                             padding: 30px;
//                             color: #111;
//                         }

//                         h1 {
//                             text-align: center;
//                         }

//                         table {
//                             width: 100%;
//                             border-collapse: collapse;
//                             margin-top: 25px;
//                         }

//                         th,
//                         td {
//                             border: 1px solid #ddd;
//                             padding: 12px;
//                             text-align: left;
//                         }

//                         th {
//                             background: #111;
//                             color: white;
//                         }

//                         .total {
//                             font-weight: bold;
//                             font-size: 18px;
//                         }

//                     </style>

//                 </head>

//                 <body>

//                     <h1>
//                         Payroll Slip
//                     </h1>

//                     <p>
//                         <strong>Staff:</strong>
//                         ${staffName}
//                     </p>

//                     <p>
//                         <strong>Month:</strong>
//                         ${payroll.month}
//                     </p>

//                     <p>
//                         <strong>Academic Year:</strong>
//                         ${payroll.academic_year?.name || ""}
//                     </p>

//                     <table>

//                         <tr>
//                             <th>Basic Salary</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.basic_salary
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Bonus / Compensation</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.night_school_commission
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Salary Advance</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.salary_advance
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Income Tax</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.income_tax
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Social Security</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.social_security_tax
//                                 )}
//                             </td>
//                         </tr>

//                         <tr>
//                             <th>Total Deductions</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.total_deductions
//                                 )}
//                             </td>
//                         </tr>

//                         <tr class="total">
//                             <th>Total Paid</th>
//                             <td>
//                                 ${formatMoney(
//                                     payroll.total_paid
//                                 )}
//                             </td>
//                         </tr>

//                     </table>

//                 </body>

//             </html>

//         `);


//         newWindow.document.close();

//         newWindow.print();

//     };


//     /*
//     |--------------------------------------------------------------------------
//     | FILTERED PAYROLLS
//     |--------------------------------------------------------------------------
//     */

//     const filteredPayrolls =
//         useMemo(() => {

//             return payrolls.filter(
//                 (payroll) => {

//                     const staffName =
//                         getStaffName(payroll);


//                     const matchesSearch =
//                         !filters.search ||

//                         staffName
//                             .toLowerCase()
//                             .includes(
//                                 filters.search
//                                     .toLowerCase()
//                             );


//                     const matchesMonth =
//                         !filters.month ||

//                         payroll.month
//                             ?.toLowerCase() ===
//                         filters.month
//                             .toLowerCase();


//                     return (

//                         matchesSearch &&

//                         matchesMonth

//                     );

//                 }
//             );

//         }, [
//             payrolls,
//             filters,
//         ]);


//     /*
//     |--------------------------------------------------------------------------
//     | STATISTICS
//     |--------------------------------------------------------------------------
//     */

//     const statistics =
//         useMemo(() => {

//             const totalPayroll =
//                 filteredPayrolls.reduce(
//                     (
//                         total,
//                         item
//                     ) =>

//                         total +
//                         Number(
//                             item.total_paid || 0
//                         ),

//                     0
//                 );


//             const totalDeductions =
//                 filteredPayrolls.reduce(
//                     (
//                         total,
//                         item
//                     ) =>

//                         total +
//                         Number(
//                             item.total_deductions || 0
//                         ),

//                     0
//                 );


//             const totalAdvances =
//                 filteredPayrolls.reduce(
//                     (
//                         total,
//                         item
//                     ) =>

//                         total +
//                         Number(
//                             item.salary_advance || 0
//                         ),

//                     0
//                 );


//             return {

//                 totalPayroll,

//                 totalDeductions,

//                 totalAdvances,

//                 staffCount:
//                     filteredPayrolls.length,

//             };

//         }, [
//             filteredPayrolls,
//         ]);


//     /*
//     |--------------------------------------------------------------------------
//     | CHART DATA
//     |--------------------------------------------------------------------------
//     */

//     const chartData =
//         useMemo(() => {

//             const grouped =
//                 filteredPayrolls.reduce(
//                     (
//                         accumulator,
//                         item
//                     ) => {

//                         const key =
//                             item.month;


//                         if (
//                             !accumulator[key]
//                         ) {

//                             accumulator[key] = {

//                                 month:
//                                     key,

//                                 total:
//                                     0,

//                             };

//                         }


//                         accumulator[key].total +=
//                             Number(
//                                 item.total_paid || 0
//                             );


//                         return accumulator;

//                     },

//                     {}
//                 );


//             return Object.values(
//                 grouped
//             );

//         }, [
//             filteredPayrolls,
//         ]);


//     /*
//     |--------------------------------------------------------------------------
//     | TABLE COLUMNS
//     |--------------------------------------------------------------------------
//     */

//     const columns = [

//         {

//             title:
//                 "Staff Name",

//             key:
//                 "staff",

//             fixed:
//                 "left",

//             width:
//                 220,

//             render:
//                 (_, record) => (

//                     <div className="staff-cell">

//                         <div className="staff-avatar">

//                             {getStaffName(
//                                 record
//                             )
//                                 .charAt(0)
//                                 .toUpperCase()}

//                         </div>

//                         <div>

//                             <Text
//                                 strong
//                             >

//                                 {getStaffName(
//                                     record
//                                 )}

//                             </Text>

//                             <br />

//                             <Text
//                                 type="secondary"
//                                 className="staff-type"
//                             >

//                                 {record.teacher
//                                     ? "Teacher"
//                                     : "Support Staff"}

//                             </Text>

//                         </div>

//                     </div>

//                 ),

//         },


//         {

//             title:
//                 "Month",

//             dataIndex:
//                 "month",

//             key:
//                 "month",

//             width:
//                 120,

//             render:
//                 (month) => (

//                     <Tag
//                         color="orange"
//                     >

//                         {month}

//                     </Tag>

//                 ),

//         },


//         {

//             title:
//                 "Academic Year",

//             key:
//                 "academic_year",

//             width:
//                 150,

//             render:
//                 (_, record) =>

//                     record.academic_year?.name || "-",

//         },


//         {

//             title:
//                 "Basic Salary",

//             dataIndex:
//                 "basic_salary",

//             key:
//                 "basic_salary",

//             width:
//                 140,

//             render:
//                 (value) =>

//                     formatMoney(
//                         value
//                     ),

//         },


//         {

//             title:
//                 "Bonus",

//             dataIndex:
//                 "night_school_commission",

//             key:
//                 "bonus",

//             width:
//                 140,

//             render:
//                 (value) =>

//                     formatMoney(
//                         value
//                     ),

//         },


//         {

//             title:
//                 "Deductions",

//             dataIndex:
//                 "total_deductions",

//             key:
//                 "deductions",

//             width:
//                 140,

//             render:
//                 (value) => (

//                     <Text
//                         type="danger"
//                         strong
//                     >

//                         {formatMoney(
//                             value
//                         )}

//                     </Text>

//                 ),

//         },


//         {

//             title:
//                 "Advance",

//             dataIndex:
//                 "salary_advance",

//             key:
//                 "advance",

//             width:
//                 140,

//             render:
//                 (value) => (

//                     <Text
//                         type="danger"
//                         strong
//                     >

//                         {formatMoney(
//                             value
//                         )}

//                     </Text>

//                 ),

//         },


//         {

//             title:
//                 "Income Tax",

//             dataIndex:
//                 "income_tax",

//             key:
//                 "income_tax",

//             width:
//                 140,

//             render:
//                 (value) =>

//                     formatMoney(
//                         value
//                     ),

//         },


//         {

//             title:
//                 "Social Security",

//             dataIndex:
//                 "social_security_tax",

//             key:
//                 "social_security",

//             width:
//                 150,

//             render:
//                 (value) =>

//                     formatMoney(
//                         value
//                     ),

//         },


//         {

//             title:
//                 "Total Paid",

//             dataIndex:
//                 "total_paid",

//             key:
//                 "total_paid",

//             width:
//                 150,

//             render:
//                 (value) => (

//                     <Text
//                         strong
//                         className="total-paid"
//                     >

//                         {formatMoney(
//                             value
//                         )}

//                     </Text>

//                 ),

//         },


//         {

//             title:
//                 "Actions",

//             key:
//                 "actions",

//             fixed:
//                 "right",

//             width:
//                 160,

//             render:
//                 (_, record) => (

//                     <Space>

//                         <Button

//                             type="text"

//                             icon={
//                                 <EditOutlined />
//                             }

//                             onClick={() =>
//                                 openEditModal(
//                                     record
//                                 )
//                             }

//                         />

//                         <Button

//                             type="text"

//                             danger

//                             icon={
//                                 <DeleteOutlined />
//                             }

//                             onClick={() =>
//                                 handleDelete(
//                                     record.id
//                                 )
//                             }

//                         />

//                         <Button

//                             type="text"

//                             icon={
//                                 <PrinterOutlined />
//                             }

//                             onClick={() =>
//                                 handlePrint(
//                                     record
//                                 )
//                             }

//                         />

//                     </Space>

//                 ),

//         },

//     ];


//     return (

//         <Layout
//             className="payroll-page"
//         >

//             <Content
//                 className="payroll-content"
//             >

//                 {/* HEADER */}

//                 <div
//                     className="payroll-header"
//                 >

//                     <div>

//                         <Text
//                             className="eyebrow"
//                         >

//                             FINANCE / PAYROLL

//                         </Text>


//                         <Title
//                             level={2}
//                             className="page-title"
//                         >

//                             Staff Payroll Management

//                         </Title>


//                         <Text
//                             type="secondary"
//                         >

//                             Manage staff compensation, deductions,
//                             salary advances and payroll reporting.

//                         </Text>

//                     </div>


//                     <div
//                         className="year-badge"
//                     >

//                         <Text>
//                             Academic Year
//                         </Text>

//                         <strong>
//                             {selectedYear?.name || "Not Selected"}
//                         </strong>

//                     </div>

//                 </div>


//                 {/* STATISTICS */}

//                 <Row
//                     gutter={[
//                         16,
//                         16,
//                     ]}
//                     className="stats-row"
//                 >

//                     <Col
//                         xs={24}
//                         sm={12}
//                         lg={6}
//                     >

//                         <Card
//                             className="stat-card orange-card"
//                         >

//                             <Statistic

//                                 title="Total Payroll"

//                                 value={
//                                     statistics.totalPayroll
//                                 }

//                                 precision={2}

//                                 prefix="$"

//                             />

//                             <DollarOutlined
//                                 className="stat-icon"
//                             />

//                         </Card>

//                     </Col>


//                     <Col
//                         xs={24}
//                         sm={12}
//                         lg={6}
//                     >

//                         <Card
//                             className="stat-card dark-card"
//                         >

//                             <Statistic

//                                 title="Staff Records"

//                                 value={
//                                     statistics.staffCount
//                                 }

//                                 prefix={
//                                     <TeamOutlined />
//                                 }

//                             />

//                             <TeamOutlined
//                                 className="stat-icon"
//                             />

//                         </Card>

//                     </Col>


//                     <Col
//                         xs={24}
//                         sm={12}
//                         lg={6}
//                     >

//                         <Card
//                             className="stat-card light-card"
//                         >

//                             <Statistic

//                                 title="Total Deductions"

//                                 value={
//                                     statistics.totalDeductions
//                                 }

//                                 precision={2}

//                                 prefix="$"

//                             />

//                             <RiseOutlined
//                                 className="stat-icon"
//                             />

//                         </Card>

//                     </Col>


//                     <Col
//                         xs={24}
//                         sm={12}
//                         lg={6}
//                     >

//                         <Card
//                             className="stat-card black-card"
//                         >

//                             <Statistic

//                                 title="Salary Advances"

//                                 value={
//                                     statistics.totalAdvances
//                                 }

//                                 precision={2}

//                                 prefix="$"

//                             />

//                             <WalletOutlined
//                                 className="stat-icon"
//                             />

//                         </Card>

//                     </Col>

//                 </Row>


//                 {/* SALARY ADVANCE */}

//                 {showSalaryAdvance ? (

//                     <Card>

//                         <Button

//                             icon={
//                                 <ReloadOutlined />
//                             }

//                             onClick={() =>
//                                 setShowSalaryAdvance(
//                                     false
//                                 )
//                             }

//                             className="back-button"

//                         >

//                             Back To Payroll

//                         </Button>


//                         <SalaryAdvance />

//                     </Card>

//                 ) : (

//                     <>

//                         {/* TOOLBAR */}

//                         <Card
//                             className="toolbar-card"
//                         >

//                             <Row
//                                 gutter={[
//                                     12,
//                                     12,
//                                 ]}
//                                 align="middle"
//                             >

//                                 <Col
//                                     xs={24}
//                                     md={8}
//                                 >

//                                     <Input

//                                         size="large"

//                                         prefix={
//                                             <SearchOutlined />
//                                         }

//                                         placeholder="Search staff name..."

//                                         value={
//                                             filters.search
//                                         }

//                                         onChange={(
//                                             event
//                                         ) =>

//                                             setFilters(
//                                                 (
//                                                     previous
//                                                 ) => ({

//                                                     ...previous,

//                                                     search:
//                                                         event
//                                                             .target
//                                                             .value,

//                                                 })
//                                             )

//                                         }

//                                         allowClear

//                                     />

//                                 </Col>


//                                 <Col
//                                     xs={24}
//                                     sm={12}
//                                     md={5}
//                                 >

//                                     <Select

//                                         size="large"

//                                         placeholder="Filter by month"

//                                         value={
//                                             filters.month ||
//                                             undefined
//                                         }

//                                         onChange={(
//                                             value
//                                         ) =>

//                                             setFilters(
//                                                 (
//                                                     previous
//                                                 ) => ({

//                                                     ...previous,

//                                                     month:
//                                                         value ||
//                                                         "",

//                                                 })
//                                             )

//                                         }

//                                         allowClear

//                                         options={
//                                             MONTHS.map(
//                                                 (
//                                                     month
//                                                 ) => ({

//                                                     label:
//                                                         month,

//                                                     value:
//                                                         month,

//                                                 })
//                                             )
//                                         }

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                     />

//                                 </Col>


//                                 <Col
//                                     xs={24}
//                                     sm={12}
//                                     md={11}
//                                 >

//                                     <Space
//                                         wrap
//                                         className="toolbar-actions"
//                                     >

//                                         <Button

//                                             icon={
//                                                 <WalletOutlined />
//                                             }

//                                             onClick={() =>
//                                                 setShowSalaryAdvance(
//                                                     true
//                                                 )
//                                             }

//                                         >

//                                             Salary Advances

//                                         </Button>


//                                         <Button

//                                             type="primary"

//                                             icon={
//                                                 <PlusOutlined />
//                                             }

//                                             onClick={
//                                                 openCreateModal
//                                             }

//                                         >

//                                             Create Payroll

//                                         </Button>


//                                         <PDFDownloadLink

//                                             document={

//                                                 <PayrollSlipGenerationPDF

//                                                     payrolls={
//                                                         filteredPayrolls
//                                                     }

//                                                     month={
//                                                         filters.month
//                                                     }

//                                                     academicYear={
//                                                         selectedYear?.name
//                                                     }

//                                                 />

//                                             }

//                                             fileName={

//                                                 `Payslips_${

//                                                     filters.month ||
//                                                     "All_Months"

//                                                 }_${

//                                                     selectedYear?.name ||
//                                                     "Academic_Year"

//                                                 }.pdf`

//                                             }

//                                         >

//                                             {({
//                                                 loading,
//                                             }) => (

//                                                 <Button

//                                                     icon={
//                                                         <FilePdfOutlined />
//                                                     }

//                                                     danger

//                                                 >

//                                                     {loading

//                                                         ? "Generating..."

//                                                         : "Download PDF"}

//                                                 </Button>

//                                             )}

//                                         </PDFDownloadLink>


//                                     </Space>

//                                 </Col>

//                             </Row>

//                         </Card>


//                         {/* TABLE */}

//                         <Card
//                             className="table-card"
//                         >

//                             <Table

//                                 rowKey="id"

//                                 loading={
//                                     tableLoading
//                                 }

//                                 columns={
//                                     columns
//                                 }

//                                 dataSource={
//                                     filteredPayrolls
//                                 }

//                                 scroll={{
//                                     x:
//                                         1650,
//                                 }}

//                                 pagination={{
//                                     pageSize:
//                                         10,

//                                     showSizeChanger:
//                                         true,

//                                     pageSizeOptions:
//                                         [
//                                             "10",
//                                             "20",
//                                             "50",
//                                             "100",
//                                         ],

//                                     showTotal:
//                                         (
//                                             total
//                                         ) =>

//                                             `${total} payroll records`,

//                                 }}

//                                 locale={{

//                                     emptyText: (

//                                         <Empty

//                                             description={
//                                                 selectedYear?.id

//                                                     ? "No payroll records found"

//                                                     : "Please select an academic year"

//                                             }

//                                         />

//                                     ),

//                                 }}

//                             />

//                         </Card>


//                         {/* CHART */}

//                         <Card
//                             className="chart-card"
//                         >

//                             <div
//                                 className="section-heading"
//                             >

//                                 <div>

//                                     <Title
//                                         level={4}
//                                     >

//                                         Monthly Payroll Summary

//                                     </Title>

//                                     <Text
//                                         type="secondary"
//                                     >

//                                         Total payroll paid by month

//                                     </Text>

//                                 </div>

//                             </div>


//                             <div
//                                 className="chart-wrapper"
//                             >

//                                 <ResponsiveContainer
//                                     width="100%"
//                                     height="100%"
//                                 >

//                                     <BarChart
//                                         data={
//                                             chartData
//                                         }
//                                     >

//                                         <XAxis
//                                             dataKey="month"
//                                         />

//                                         <YAxis />

//                                         <Tooltip />

//                                         <Bar
//                                             dataKey="total"
//                                             fill="#f97316"
//                                             radius={[
//                                                 8,
//                                                 8,
//                                                 0,
//                                                 0,
//                                             ]}
//                                         />

//                                     </BarChart>

//                                 </ResponsiveContainer>

//                             </div>

//                         </Card>

//                     </>

//                 )}


//                 {/* CREATE / EDIT MODAL */}

//                 <Modal

//                     open={
//                         openModal
//                     }

//                     title={

//                         <div
//                             className="modal-title"
//                         >

//                             <div
//                                 className="modal-title-icon"
//                             >

//                                 <DollarOutlined />

//                             </div>


//                             <div>

//                                 <Title
//                                     level={4}
//                                 >

//                                     {editingPayroll

//                                         ? "Edit Payroll"

//                                         : "Create Payroll"}

//                                 </Title>

//                                 <Text
//                                     type="secondary"
//                                 >

//                                     {selectedYear?.name}

//                                 </Text>

//                             </div>

//                         </div>

//                     }

//                     onCancel={
//                         closeModal
//                     }

//                     footer={null}

//                     width={
//                         850
//                     }

//                     centered

//                     destroyOnClose

//                 >

//                     <Alert

//                         message="Select only one staff member"

//                         description="A payroll record can belong to either a Teacher or Support Staff, not both."

//                         type="info"

//                         showIcon

//                         style={{
//                             marginBottom:
//                                 24,
//                         }}

//                     />


//                     <Form

//                         form={
//                             form
//                         }

//                         layout="vertical"

//                         onFinish={
//                             handleSubmit
//                         }

//                     >

//                         <Row
//                             gutter={[
//                                 16,
//                                 0,
//                             ]}
//                         >

//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item
//                                     label="Teacher"
//                                     name="teacher_id"
//                                 >

//                                     <Select

//                                         showSearch

//                                         allowClear

//                                         placeholder="Select teacher"

//                                         disabled={
//                                             selectedStaffType ===
//                                             "support_staff"
//                                         }

//                                         optionFilterProp="label"

//                                         onChange={
//                                             handleTeacherChange
//                                         }

//                                         options={

//                                             teachers.map(
//                                                 (
//                                                     teacher
//                                                 ) => ({

//                                                     label:
//                                                         teacher.full_name,

//                                                     value:
//                                                         teacher.id,

//                                                 })
//                                             )

//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item
//                                     label="Support Staff"
//                                     name="support_staff_id"
//                                 >

//                                     <Select

//                                         showSearch

//                                         allowClear

//                                         placeholder="Select support staff"

//                                         disabled={
//                                             selectedStaffType ===
//                                             "teacher"
//                                         }

//                                         optionFilterProp="label"

//                                         onChange={
//                                             handleSupportStaffChange
//                                         }

//                                         options={

//                                             supportStaff.map(
//                                                 (
//                                                     staff
//                                                 ) => ({

//                                                     label:
//                                                         staff.full_name,

//                                                     value:
//                                                         staff.id,

//                                                 })
//                                             )

//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                <Form.Item
//     label="Payroll Month"
//     name="month"
//     rules={[
//         {
//             required: true,
//             message: "Please select a month.",
//         },
//     ]}
// >
// <Select
//     onChange={handleMonthChange}
//     options={MONTHS.map((month) => ({
//         label: month,
//         value: month,
//     }))}
// />
// </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Basic Salary"

//                                     name="basic_salary"

//                                 >

//                                     <InputNumber

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                         prefix="$"

//                                         readOnly

//                                         min={
//                                             0
//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Bonus / Compensation"

//                                     name="night_school_commission"

//                                 >

//                                     <InputNumber

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                         prefix="$"

//                                         min={
//                                             0
//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                             <Form.Item
//     label="Salary Advance Deduction"
//     name="salary_advance"
//     extra={
//         salaryAdvanceBalance > 0
//             ? `Outstanding balance: $${salaryAdvanceBalance.toFixed(2)}`
//             : "No outstanding salary advance"
//     }
// >
//     <InputNumber
//         style={{
//             width: "100%",
//         }}
//         min={0}
//         precision={2}
//         prefix="$"
//         disabled={salaryAdvanceLoading}
//         onChange={(value) => {
//             const deduction = Number(value || 0);

//             if (deduction > salaryAdvanceBalance) {
//                 message.warning(
//                     "The deduction cannot be greater than the outstanding salary advance."
//                 );
//             }
//         }}
//     />
// </Form.Item>
// <Form.Item
//     name="salary_advance_record"
//     hidden
// >
//     <Input />
// </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Income Tax"

//                                     name="income_tax"

//                                 >

//                                     <InputNumber

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                         prefix="$"

//                                         min={
//                                             0
//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>


//                             <Col
//                                 xs={24}
//                                 md={12}
//                             >

//                                 <Form.Item

//                                     label="Social Security Tax"

//                                     name="social_security_tax"

//                                 >

//                                     <InputNumber

//                                         style={{
//                                             width:
//                                                 "100%",
//                                         }}

//                                         prefix="$"

//                                         min={
//                                             0
//                                         }

//                                     />

//                                 </Form.Item>

//                             </Col>

//                         </Row>


//                         <div
//                             className="modal-footer"
//                         >

//                             <Button
//                                 onClick={
//                                     closeModal
//                                 }
//                             >

//                                 Cancel

//                             </Button>


//                             <Button

//                                 type="primary"

//                                 htmlType="submit"

//                                 loading={
//                                     loading
//                                 }

//                             >

//                                 {editingPayroll

//                                     ? "Update Payroll"

//                                     : "Create Payroll"}

//                             </Button>

//                         </div>

//                     </Form>

//                 </Modal>

//             </Content>

//         </Layout>

//     );

// }


// export default Payroll;